// Canonical catalog of app-defect issue codes for main.app_defect.
// Every defect row carries an issue code from this catalog plus a fix
// category from projects/shared/apps/appFixCategories.js.
// Detector-emitted codes keep the identities they had in the retired
// cirra.mcp_contract_defect ledger so migrated rows stay comparable.

const APP_DEFECT_STATUSES = Object.freeze(['open', 'fixing', 'proposed', 'resolved', 'dismissed', 'blocked']);
const APP_DEFECT_SEVERITIES = Object.freeze(['low', 'medium', 'high', 'critical']);
const APP_DEFECT_SOURCES = Object.freeze(['detector', 'admin', 'cirra-agent', 'thread-miner']);

const appDefectCatalog = Object.freeze({
  PAGINATION_LIMIT_MAX_MISSING: Object.freeze({
    issueCode: 'PAGINATION_LIMIT_MAX_MISSING',
    category: 'pagination',
    defaultSeverity: 'high',
    surface: 'execute_pagination',
    summary: 'Configured pagination max limit is missing while provider behavior proves a lower enforced bound.',
    fingerprintRule: 'issue_only',
    fingerprintKeys: [],
    requiredEvidenceKeys: ['appliedLimit', 'observedRowCount', 'providerCursor'],
    detectorScope: 'action'
  }),
  PAGINATION_LIMIT_MAX_MISMATCH_PROVIDER_CLAMP: Object.freeze({
    issueCode: 'PAGINATION_LIMIT_MAX_MISMATCH_PROVIDER_CLAMP',
    category: 'pagination',
    defaultSeverity: 'high',
    surface: 'execute_pagination',
    summary: 'Configured pagination max limit is wrong and provider behavior proves a different enforced clamp.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['observedProviderLimit'],
    requiredEvidenceKeys: ['configuredMaxLimit', 'appliedLimit', 'observedRowCount', 'observedProviderLimit', 'providerCursor'],
    detectorScope: 'action'
  }),
  PAGINATION_LIMIT_MAX_REJECTED_BY_PROVIDER: Object.freeze({
    issueCode: 'PAGINATION_LIMIT_MAX_REJECTED_BY_PROVIDER',
    category: 'pagination',
    defaultSeverity: 'high',
    surface: 'execute_pagination',
    summary: 'A pagination limit is rejected by the provider while a smaller limit succeeds.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['providerStatus', 'providerErrorCode'],
    requiredEvidenceKeys: ['rejectedLimit', 'acceptedLimit', 'providerStatus'],
    detectorScope: 'action'
  }),
  PAGINATION_CURSOR_CONTRACT_MISMATCH: Object.freeze({
    issueCode: 'PAGINATION_CURSOR_CONTRACT_MISMATCH',
    category: 'pagination',
    defaultSeverity: 'medium',
    surface: 'execute_pagination',
    summary: 'Declared pagination cursor contract does not match observed provider behavior.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['cursorReason'],
    requiredEvidenceKeys: ['cursorReason'],
    detectorScope: 'action'
  }),
  REQUEST_MAPPER_CONTRACT_FAILURE: Object.freeze({
    issueCode: 'REQUEST_MAPPER_CONTRACT_FAILURE',
    category: 'executionContract',
    defaultSeverity: 'high',
    surface: 'execute_request_mapping',
    summary: 'The app request mapper failed before a valid provider request could be completed.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['runtimeCode'],
    requiredEvidenceKeys: ['runtimeCode'],
    detectorScope: 'action'
  }),
  RESPONSE_MAPPER_CONTRACT_FAILURE: Object.freeze({
    issueCode: 'RESPONSE_MAPPER_CONTRACT_FAILURE',
    category: 'responseSchema',
    defaultSeverity: 'high',
    surface: 'execute_response_mapping',
    summary: 'The app response mapper failed or returned an invalid response shape.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['runtimeCode'],
    requiredEvidenceKeys: ['runtimeCode'],
    detectorScope: 'action'
  }),
  RESPONSE_ERROR_DETECTION_CONTRACT_MISMATCH: Object.freeze({
    issueCode: 'RESPONSE_ERROR_DETECTION_CONTRACT_MISMATCH',
    category: 'errorDetection',
    defaultSeverity: 'high',
    surface: 'execute_response_mapping',
    summary: 'The app response mapper marked a provider success status as a failure.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['mismatchReason'],
    requiredEvidenceKeys: ['mismatchReason', 'providerStatus'],
    detectorScope: 'action'
  }),
  LOOKUP_SOURCE_MISSING: Object.freeze({
    issueCode: 'LOOKUP_SOURCE_MISSING',
    category: 'lookups',
    defaultSeverity: 'high',
    surface: 'lookup_resolution',
    summary: 'Lookup argument is declared without a valid source definition.',
    fingerprintRule: 'issue_only',
    fingerprintKeys: [],
    requiredEvidenceKeys: ['lookupType'],
    detectorScope: 'argument'
  }),
  LOOKUP_SOURCE_ACTION_NOT_FOUND: Object.freeze({
    issueCode: 'LOOKUP_SOURCE_ACTION_NOT_FOUND',
    category: 'lookups',
    defaultSeverity: 'high',
    surface: 'lookup_resolution',
    summary: 'Lookup argument points to a non-existent lookup action.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['lookupActionId'],
    requiredEvidenceKeys: ['lookupActionId'],
    detectorScope: 'argument'
  }),
  LOOKUP_SOURCE_PAYLOAD_NOT_MAPPABLE: Object.freeze({
    issueCode: 'LOOKUP_SOURCE_PAYLOAD_NOT_MAPPABLE',
    category: 'lookups',
    defaultSeverity: 'medium',
    surface: 'lookup_resolution',
    summary: 'Lookup source payload shape does not match the declared lookup contract.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['sourcePayloadShape'],
    requiredEvidenceKeys: ['sourcePayloadShape', 'lookupLabel', 'lookupValue'],
    detectorScope: 'argument'
  }),
  LOOKUP_SOURCE_CONTRACT_CONFLICT: Object.freeze({
    issueCode: 'LOOKUP_SOURCE_CONTRACT_CONFLICT',
    category: 'lookups',
    defaultSeverity: 'low',
    surface: 'lookup_resolution',
    summary: 'Lookup argument declares conflicting source definitions.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['conflictType'],
    requiredEvidenceKeys: ['conflictType'],
    detectorScope: 'argument'
  }),
  CREDENTIAL_TEST_ACTION_MISSING: Object.freeze({
    issueCode: 'CREDENTIAL_TEST_ACTION_MISSING',
    category: 'credentialTestAction',
    defaultSeverity: 'high',
    surface: 'connection_setup',
    summary: 'App requires authenticated connection setup but does not expose a usable credential test action.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['reason'],
    requiredEvidenceKeys: ['reason'],
    detectorScope: 'app'
  }),
  EXECUTION_HTTP_4XX_CONTRACT_SUSPECTED: Object.freeze({
    issueCode: 'EXECUTION_HTTP_4XX_CONTRACT_SUSPECTED',
    category: 'executionContract',
    defaultSeverity: 'medium',
    surface: 'execute_action',
    summary: 'Provider returned an HTTP 4xx execution failure that may reflect an app-contract mismatch.',
    fingerprintRule: 'evidence_keys',
    fingerprintKeys: ['providerStatus', 'providerErrorCode', 'normalizedMessageTemplate'],
    requiredEvidenceKeys: ['providerStatus', 'normalizedMessageTemplate'],
    detectorScope: 'action'
  }),
  ADMIN_REPORT: Object.freeze({
    issueCode: 'ADMIN_REPORT',
    category: null,
    categoryFromEvent: true,
    defaultSeverity: 'medium',
    surface: 'admin_report',
    summary: 'A person reported an app defect from the platform.',
    fingerprintRule: 'category',
    fingerprintKeys: [],
    requiredEvidenceKeys: [],
    detectorScope: 'app'
  })
});

const getAppDefectDefinition = (issueCode) => {
  const normalizedIssueCode = String(issueCode || '')
    .trim()
    .toUpperCase();

  if (!normalizedIssueCode) {
    return null;
  }

  return appDefectCatalog[normalizedIssueCode] || null;
};

export { APP_DEFECT_SEVERITIES, APP_DEFECT_SOURCES, APP_DEFECT_STATUSES, appDefectCatalog, getAppDefectDefinition };
